import { authApi, siteApi } from "@api";
import { IconMSIIdCard } from "@assets/icons";
import { DEFAULT_ERROR_MESSAGE } from "@constants/misc";
import useAuthSession from "@hooks/useAuthSession";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import useFundingProfile from "@hooks/useFundingProfile";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePaymentInfo from "@hooks/usePaymentInfo";
import { AppDispatch } from "@stores";
import authSlice from "@stores/auth/slices";
import { Currency } from "@types";
import {
  getBankAccountDuplicateKey,
  getBankAccountErrorKey,
  getUniqueIdentifierErrorKey,
  type PidType,
} from "@utils/funding-profile-field-names";
import { mapBankFundingDetails } from "@utils/funding-profile-mapping";
import { buildPersonalInfoRequest } from "@utils/personal-info";
import { TFunction } from "i18next";
import {
  APIErrorCodes,
  Button,
  CreateFundingProfileRequest,
  FundingProfileType,
  MemberBankFundingType,
  MemberSettingFundingMode,
  Prompt,
  cn,
  useFieldHelperText,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdAccountBalance } from "react-icons/md";
import { useDispatch } from "react-redux";
import { BankAccountInnerForm, EWalletInnerForm } from "./components";
import { useFundingProfileFormController } from "./hooks";
import { FundingProfileFieldValues } from "./types";

export interface AddFundingProfileFormRef {
  resetForm: () => void;
}

interface AddFundingProfileFormProps {
  onSubmitSuccess?: () => void;
  variant?: "contained" | "flat";
  defaultFundingMode: MemberSettingFundingMode;
}

const SUPPORTED_ERROR_CODES = [
  APIErrorCodes.FundingProfile.AlreadyAdded,
  APIErrorCodes.FundingProfile.LinkedToAnotherMember,
  APIErrorCodes.Member.UniqueInfoConflict,
];

const REQUIRE_LEGAL_NAME_CURRENCIES = [Currency.IDR];

/**
 * This form is used to add a new funding profile.
 * Based on the funding mode, it will render the appropriate inner form.
 * fundingMode:
 * - e-wallet-only: EWalletInnerForm
 * - bank-only: BankAccountInnerForm
 */
const AddFundingProfileForm = forwardRef<AddFundingProfileFormRef, AddFundingProfileFormProps>(
  ({ onSubmitSuccess, variant = "contained", defaultFundingMode: fundingMode }, ref) => {
    const { t } = useTranslation();
    const { getPaymentMethodByKey } = usePaymentInfo();
    const { createFundingProfile, isCreatingFundingProfile, fundingProfileList } = useFundingProfile();
    const [updatePersonalInfo, { isLoading: isUpdatingPersonalInfo }] = siteApi.useUpdatePersonalInfoMutation();
    const {
      form: {
        control,
        handleSubmit,
        formState,
        formState: { errors, dirtyFields, submitCount },
        reset,
        getValues,
        setError,
        watch,
        clearErrors,
        trigger,
      },
    } = useFundingProfileFormController({ defaultFundingMode: fundingMode });
    const { handleApiResponse } = useHandleApiResponse({ toast });
    const [isDisplayCloseConfirmDialog, setIsDisplayCloseConfirmDialog] = useState(false);
    const [savedFormData, setSavedFormData] = useState<Partial<FundingProfileFieldValues> | null>(null);
    const { nameMode, currency, phonePrefix } = useMemberSettings();

    const dispatch = useDispatch<AppDispatch>();
    const { legalName, self } = useAuthSession();
    const refShouldDisplayLegalName = useRef(false);

    // Capture form validation errors
    useCaptureFormValidationErrors(
      {
        formState,
        watch,
      },
      { feature: "add-funding-profile" },
    );

    const fieldHelperTextManager = useFieldHelperText({
      submitCount: submitCount,
      trigger: trigger,
      errors: errors,
    });

    useEffect(() => {
      if (!legalName) {
        refShouldDisplayLegalName.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Expose the reset function to the parent component
    useImperativeHandle(ref, () => ({
      resetForm: () => {
        reset();
      },
    }));

    const onSubmit = async () => {
      const data = getValues();

      // Update personal info (if provided) - validate unique identifiers before showing dialog
      if (
        !!data.accountName ||
        (!!data.firstName && !!data.lastName) ||
        !!data.pan ||
        !!data.cpf ||
        !!data.curp ||
        (!!data.pidType && !!data.pidNumber)
      ) {
        const request = buildPersonalInfoRequest(data, nameMode);
        const response = await updatePersonalInfo(request);

        if (response.error) {
          const errorCode = (response.error as any)?.data?.error?.message;

          // Handle unique info conflict error
          if (errorCode === APIErrorCodes.Member.UniqueInfoConflict) {
            const errorKey = getUniqueIdentifierErrorKey({
              pan: data.pan,
              cpf: data.cpf,
              curp: data.curp,
              pidType: data.pidType as PidType | undefined,
              pidNumber: data.pidNumber,
            });

            // Set error on the appropriate field
            if (data.pan) {
              setError("pan", {
                message: errorKey,
                type: "manual",
              });
            } else if (data.cpf) {
              setError("cpf", {
                message: errorKey,
                type: "manual",
              });
            } else if (data.curp) {
              setError("curp", {
                message: errorKey,
                type: "manual",
              });
            } else if (data.pidType && data.pidNumber) {
              setError("pidNumber", {
                message: errorKey,
                type: "manual",
              });
            }

            // Don't show dialog on error
            return;
          }

          // Handle other errors
          handleApiResponse(response);
          return;
        }
      }

      // Only show dialog if personal info validation passed (or wasn't needed)
      setIsDisplayCloseConfirmDialog(true);
    };

    const closeDialog = useCallback(() => {
      setIsDisplayCloseConfirmDialog(false);

      // Clean up and reset form after animation completes
      const cleanupDelay = 400;

      setTimeout(() => {
        setSavedFormData(null);
      }, cleanupDelay);
    }, []);

    const shouldDisplayNameWarning = useMemo(() => {
      return REQUIRE_LEGAL_NAME_CURRENCIES.includes(currency as Currency);
    }, [currency]);

    const handleCreateFundingProfile = async () => {
      const data = getValues();

      setSavedFormData({
        accountType: data?.accountType,
        accountNo: data?.accountNo,
        bankType: data?.bankType,
        pixKeyType: data?.pixKeyType,
        accountName: data?.accountName,
        firstName: data?.firstName,
        lastName: data?.lastName,
      });

      // Country-specific info and accNo mapping
      const mapped = mapBankFundingDetails({ currency: currency as Currency, data, phonePrefix });

      // create funding profile
      const fundingProfileType = getPaymentMethodByKey(data.accountType, data.bankType as MemberBankFundingType)?.type;
      if (!fundingProfileType) {
        toast.error(t(DEFAULT_ERROR_MESSAGE));
        return;
      }
      const request: CreateFundingProfileRequest = {
        key: data.accountType,
        accNo: data.accountNo.trim(),
        isPrimary: data.isDefault,
        type: fundingProfileType,
      };

      if (mapped.info) request.info = mapped.info as any;
      if (mapped.accNo) request.accNo = mapped.accNo;

      const response = await createFundingProfile(request);

      const errorCode = (response?.error as any)?.data?.error?.message;

      // Handle funding profile errors - use existing error handling for AlreadyAdded and LinkedToAnotherMember
      if (response.error && SUPPORTED_ERROR_CODES.includes(errorCode)) {
        const errorMessage = getSupportedErrorMessage(
          errorCode,
          t,
          fundingMode,
          currency as Currency,
          data.bankType as MemberBankFundingType | undefined,
        );
        if (!!errorMessage) {
          setError("accountNo", { message: `backend:${errorMessage}` });
        }
        closeDialog();
        return;
      }

      handleApiResponse(response, {
        onSuccess: async () => {
          const result = await dispatch(
            authApi.endpoints.getProfile.initiate(undefined, {
              forceRefetch: true,
            }),
          );
          if (result.data) {
            dispatch(authSlice.actions.updateProfile(result.data));
          }
          closeDialog();

          onSubmitSuccess?.();
          toast.success(t(`account added successfully!`));
          refShouldDisplayLegalName.current = false;
        },
      });
    };

    const accountType$ = watch("accountType");
    const accountNo$ = watch("accountNo");
    const bankType$ = watch("bankType");
    const pixKeyType$ = watch("pixKeyType");
    const accountName$ = watch("accountName");
    const firstName$ = watch("firstName");
    const lastName$ = watch("lastName");

    const confirmFundingProfile = useMemo(() => {
      const data = savedFormData || {
        accountType: accountType$,
        accountNo: accountNo$,
        bankType: bankType$,
        pixKeyType: pixKeyType$,
        accountName: accountName$,
        firstName: firstName$,
        lastName: lastName$,
      };

      const paymentMethod = getPaymentMethodByKey(data.accountType, data.bankType as MemberBankFundingType);

      const icon = paymentMethod?.noIcon ? null : !paymentMethod?.icon ? (
        <MdAccountBalance
          className="text-content-base"
          size={24}
        />
      ) : (
        <img
          src={paymentMethod?.icon || ""}
          alt={paymentMethod?.name || ""}
          className="h-8 w-8 shrink-0"
        />
      );

      const name = paymentMethod?.displayName;

      return {
        icon,
        name,
        accountNo: data.accountNo,
        fullName: data.accountName,
        firstName: data.firstName,
        lastName: data.lastName,
        pixKeyType: data.pixKeyType,
        noIcon: paymentMethod?.noIcon,
      };
    }, [savedFormData, accountType$, accountNo$, bankType$, pixKeyType$, accountName$, firstName$, lastName$, getPaymentMethodByKey]);

    const isFormLoading = isCreatingFundingProfile || isUpdatingPersonalInfo;

    // Reset account number invalid once account type is changed
    useEffect(() => {
      const INVALID_ACCOUNT_NO_ERROR_MESSAGE = [
        `${fundingMode} already added`,
        t(`${fundingMode} is linked to another user.`),
      ];

      const accountNoError = errors.accountNo?.message || "";
      const strippedError = accountNoError.startsWith("backend:") ? accountNoError.slice(8) : accountNoError;

      if (INVALID_ACCOUNT_NO_ERROR_MESSAGE.includes(strippedError)) {
        clearErrors("accountNo");
      }

      // Also clear errors that end with "is duplicated" or "is not valid"
      if (strippedError.includes("is duplicated") || strippedError.includes("is not valid")) {
        clearErrors("accountNo");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountType$]);

    // Revalidate account number when method/discriminant changes
    useEffect(() => {
      if (dirtyFields["accountNo"]) {
        trigger("accountNo");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankType$, dirtyFields["accountNo"]]);

    const { allowEWalletEditDefault, allowBankEditDefault } = useMemo(() => {
      // if user is adding first account, don't allow to uncheck default
      const hasBankAccount = fundingProfileList.some((profile) => profile.type === FundingProfileType.Bank);
      const hasEWallet = fundingProfileList.some((profile) => profile.type === FundingProfileType.EWallet);

      return {
        allowEWalletEditDefault: hasEWallet,
        allowBankEditDefault: hasBankAccount,
      };
    }, [fundingProfileList]);

    return (
      <div
        className={cn("mt-4 flex flex-col gap-4 rounded-[6px] border-stroke-soft p-4", {
          "bg-grdMid": variant === "contained",
          "bg-transparent": variant === "flat",
          "px-0 py-0": variant === "flat",
        })}
      >
        {fundingMode === MemberSettingFundingMode.EWalletOnly && (
          <EWalletInnerForm
            errors={errors}
            control={control}
            isLoading={isCreatingFundingProfile}
            displayDisplayNameWarning={shouldDisplayNameWarning}
            displayLegalName={!legalName}
            allowEditDefault={allowEWalletEditDefault}
            fieldHelperTextManager={fieldHelperTextManager}
          />
        )}

        {fundingMode === MemberSettingFundingMode.BankOnly && (
          <BankAccountInnerForm
            errors={errors}
            control={control}
            isLoading={isCreatingFundingProfile}
            displayDisplayNameWarning={shouldDisplayNameWarning}
            displayLegalName={!legalName}
            allowEditDefault={allowBankEditDefault}
            fieldHelperTextManager={fieldHelperTextManager}
          />
        )}

        <Button
          size={variant === "flat" ? "lg" : "md"}
          onClick={handleSubmit(onSubmit)}
          loading={isFormLoading}
          className="mt-4"
          aria-label="Add funding profile submit"
        >
          {t("submit")}
        </Button>

        <Prompt
          open={isDisplayCloseConfirmDialog}
          onClose={closeDialog}
          onConfirm={handleCreateFundingProfile}
          title={`${t("confirm")}?`}
          content={
            <div className="mt-[6px]">
              <div className="flex flex-1 items-center justify-start gap-3 rounded border-stroke-soft bg-grdHigh p-3 text-start">
                {!confirmFundingProfile.noIcon && (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center">{confirmFundingProfile.icon}</div>
                )}
                <div className="flex flex-col flex-wrap items-start justify-start gap-0.5">
                  <p className="line-clamp-1 text-xs leading-4 text-content-base">{confirmFundingProfile.name}</p>
                  {!!confirmFundingProfile?.pixKeyType && (
                    <p className="text-[10px] leading-4 text-content-soft">
                      {t(`pix type`)}:{" "}
                      {t(
                        (confirmFundingProfile.pixKeyType === "evp" ? "random" : confirmFundingProfile.pixKeyType) ||
                          "",
                      )}
                    </p>
                  )}
                  <p className="text-base font-bold text-content-base">{confirmFundingProfile.accountNo}</p>
                </div>
              </div>

              {!!accountName$ && (
                <div className="mt-4 flex flex-1 items-center justify-start gap-3 rounded border-stroke-soft bg-grdHigh p-3 text-start">
                  <div className="flex items-center gap-3">
                    <IconMSIIdCard className="text-content-base" />
                    <span className="text-base leading-6 text-content-base">{accountName$}</span>
                  </div>
                </div>
              )}

              {!!firstName$ && !!lastName$ && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="flex flex-1 items-center justify-start gap-3 rounded border-stroke-soft bg-grdHigh p-3 text-start">
                    <span className="text-base leading-6 text-content-base">{firstName$}</span>
                  </div>
                  <div className="flex flex-1 items-center justify-start gap-3 rounded border-stroke-soft bg-grdHigh p-3 text-start">
                    <span className="text-base leading-6 text-content-base">{lastName$}</span>
                  </div>
                </div>
              )}
            </div>
          }
          zIndex={40}
          messages={{
            confirm: t("confirm"),
            cancel: t("back"),
          }}
          className="bg-grdMid"
          confirmLoading={isFormLoading || !!savedFormData}
        />
      </div>
    );
  },
);

const getSupportedErrorMessage = (
  errorCode:
    | typeof APIErrorCodes.FundingProfile.AlreadyAdded
    | typeof APIErrorCodes.FundingProfile.LinkedToAnotherMember,
  t: TFunction,
  fundingMode: MemberSettingFundingMode,
  currency: Currency,
  bankType?: MemberBankFundingType,
): string | null => {
  // For e-wallet funding mode, use the generic e-wallet messages
  if (fundingMode === MemberSettingFundingMode.EWalletOnly) {
    if (errorCode === APIErrorCodes.FundingProfile.AlreadyAdded) {
      return t(`${fundingMode} already added`);
    }
    if (errorCode === APIErrorCodes.FundingProfile.LinkedToAnotherMember) {
      return t(`${fundingMode} is linked to another user.`);
    }
    return null;
  }

  // For bank accounts, use specific field names based on currency and bank type
  if (errorCode === APIErrorCodes.FundingProfile.AlreadyAdded) {
    return getBankAccountDuplicateKey(currency, bankType);
  }

  if (errorCode === APIErrorCodes.FundingProfile.LinkedToAnotherMember) {
    return getBankAccountErrorKey(currency, bankType);
  }
  return null;
};

AddFundingProfileForm.displayName = "AddFundingProfileForm";

export default AddFundingProfileForm;
